import React from 'react';
import { Calendar } from 'primereact/calendar';
import dateUtils from '../../../../../../../utils/date-format';

interface LectureTimePickerProps {
  startTime: Date;
  endTime: Date;
  /** Planned lecture window — selected times must stay within this range */
  plannedStartTime: Date;
  plannedEndTime: Date;
  onStartTimeChange: (time: Date) => void;
  onEndTimeChange: (time: Date) => void;
}

const applyTimeOfDay = (base: Date, timeSource: Date): Date => {
  const next = new Date(base);
  next.setHours(timeSource.getHours(), timeSource.getMinutes(), 0, 0);
  return next;
};

const clampToPlannedWindow = (
  value: Date,
  plannedStart: Date,
  plannedEnd: Date,
): Date => {
  if (value.getTime() < plannedStart.getTime()) {
    return new Date(plannedStart);
  }
  if (value.getTime() > plannedEnd.getTime()) {
    return new Date(plannedEnd);
  }
  return value;
};

export const LectureTimePicker: React.FC<LectureTimePickerProps> = ({
  startTime,
  endTime,
  plannedStartTime,
  plannedEndTime,
  onStartTimeChange,
  onEndTimeChange,
}) => {
  const plannedStart = new Date(plannedStartTime);
  const plannedEnd = new Date(plannedEndTime);

  const handleStartTimeChange = (newTime: Date | null) => {
    if (!newTime) return;

    let updatedStartTime = applyTimeOfDay(startTime, newTime);
    updatedStartTime = clampToPlannedWindow(
      updatedStartTime,
      plannedStart,
      plannedEnd,
    );

    // Start cannot be after the selected end time.
    if (updatedStartTime.getTime() > endTime.getTime()) {
      updatedStartTime = new Date(endTime);
    }

    onStartTimeChange(updatedStartTime);
  };

  const handleEndTimeChange = (newTime: Date | null) => {
    if (!newTime) return;

    let updatedEndTime = applyTimeOfDay(endTime, newTime);
    updatedEndTime = clampToPlannedWindow(
      updatedEndTime,
      plannedStart,
      plannedEnd,
    );

    // End cannot be before the selected start time.
    if (updatedEndTime.getTime() < startTime.getTime()) {
      updatedEndTime = new Date(startTime);
    }

    onEndTimeChange(updatedEndTime);
  };

  const plannedRangeLabel = `${dateUtils.formatTimeFromDate(plannedStart)} – ${dateUtils.formatTimeFromDate(plannedEnd)}`;

  return (
    <div
      style={{
        marginTop: '16px',
        marginBottom: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontWeight: 'bold' }}>זמן התחלה</label>
          <Calendar
            value={startTime}
            onChange={(e) => handleStartTimeChange(e.value as Date)}
            minDate={plannedStart}
            maxDate={
              endTime.getTime() < plannedEnd.getTime() ? endTime : plannedEnd
            }
            panelStyle={{
              direction: 'ltr',
            }}
            timeOnly
            hourFormat="24"
            placeholder="בחר זמן התחלה"
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontWeight: 'bold' }}>זמן סיום</label>
          <Calendar
            value={endTime}
            onChange={(e) => handleEndTimeChange(e.value as Date)}
            minDate={
              startTime.getTime() > plannedStart.getTime()
                ? startTime
                : plannedStart
            }
            maxDate={plannedEnd}
            panelStyle={{
              direction: 'ltr',
            }}
            timeOnly
            hourFormat="24"
            placeholder="בחר זמן סיום"
          />
        </div>
      </div>

      <div
        style={{
          padding: '10px 12px',
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRight: '4px solid #3b82f6',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#1e40af',
          lineHeight: 1.5,
        }}
      >
        ניתן לבחור זמני ביצוע רק בתוך הזמן המתוכנן של ההרצאה (
        <strong>{plannedRangeLabel}</strong>). לא ניתן לחרוג מטווח זה. במקרים של
        שינוי הזמן המתוכנן, ניתן לשנות את זמן התכנון של ההרצאה
      </div>
    </div>
  );
};
